// Detect media URL type and provide embed helpers

export function detectMediaType(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (!u) return null;
  if (/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)/i.test(u)) return 'youtube';
  if (/vimeo\.com\/(\d+)/i.test(u)) return 'vimeo';
  if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(u)) return 'video';
  if (/\.(jpe?g|png|gif|webp|svg|avif)(\?.*)?$/i.test(u)) return 'image';
  // Relative uploads count as image so /uploads/foo.jpg works
  if (/^\/(uploads|images)\//i.test(u)) return 'image';
  return 'link';
}

export function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

export function getEmbedSrc(url) {
  const t = detectMediaType(url);
  if (t === 'youtube') {
    const id = getYouTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}` : null;
  }
  if (t === 'vimeo') {
    const id = getVimeoId(url);
    return id ? `https://player.vimeo.com/video/${id}` : null;
  }
  return null;
}

// Return a YouTube thumbnail URL when the input is a YouTube link, else null.
export function getYouTubeThumbnail(url, quality = 'hqdefault') {
  const id = getYouTubeId(url);
  return id ? `https://i.ytimg.com/vi/${id}/${quality}.jpg` : null;
}

// Heuristic: detect Canva *share* links that are NOT direct image URLs.
// Canva share links are HTML pages, not images — rendering them in <img> fails
// silently. We use this to surface a clear bilingual warning instead.
export function isCanvaShareLink(url) {
  if (!url || typeof url !== 'string') return false;
  return /(?:^|\.)canva\.(?:com|link|site)\//i.test(url.trim());
}

// Resolve a Library resource's media into three normalised buckets:
//   { videoUrl, imageUrls: [], thumbnailUrl }
// Provides full backwards-compatibility with the legacy `media_url` field:
//   • If media_url is a video → treat as videoUrl
//   • If media_url is a direct image → treat as the first imageUrls entry
//   • If admin already set the new fields, prefer those.
// Thumbnail fallback chain: explicit thumbnail_url → YouTube thumbnail of
// videoUrl → first imageUrls entry → null.
export function resolveLibraryMedia(resource) {
  if (!resource) return { videoUrl: '', imageUrls: [], thumbnailUrl: '' };
  const legacy = (resource.media_url || '').trim();
  let videoUrl = (resource.video_url || '').trim();
  const explicitImgs = Array.isArray(resource.image_urls)
    ? resource.image_urls.map((s) => (s || '').trim()).filter(Boolean)
    : [];
  let imageUrls = explicitImgs.slice(0, 3);

  if (!videoUrl && legacy) {
    const t = detectMediaType(legacy);
    if (t === 'youtube' || t === 'vimeo' || t === 'video') {
      videoUrl = legacy;
    } else if (t === 'image' && imageUrls.length === 0) {
      imageUrls = [legacy];
    }
  }

  let thumbnailUrl = (resource.thumbnail_url || '').trim();
  if (!thumbnailUrl) {
    thumbnailUrl = getYouTubeThumbnail(videoUrl) || (imageUrls[0] || '');
  }

  return { videoUrl, imageUrls, thumbnailUrl };
}
